import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import DebouncedSearchInput from '../components/common/DebouncedSearchInput';
import Input from '../components/common/Input';
import { useInfiniteGateEntries, useAssignDock } from '../hooks/useGateEntries';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../hooks/useClients';
import { useDebounce } from '../hooks/useDebounce';
import { gateEntryService } from '../services/gateEntryService';
import { INPUT_SANITIZERS, FIELD_VALIDATORS } from '../utils/validationHelpers';
import { GateEntry, Client } from '../types';
import GateEntryPrintView from '../components/inward-outward/GateEntryPrintView';

type StatusFilter = 'all' | 'pending_dock_assignment' | 'dock_assigned' | 'pick_slip_created' | 'dispatched';

export default function Outward() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { hasPermission, user } = useAuth();
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dockNumbers, setDockNumbers] = useState<Record<string, string>>({});
  const [printEntry, setPrintEntry] = useState<GateEntry | null>(null);
  const [highlightedId, setHighlightedId] = useState<number | string | null>(null);
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const hasProcessedState = useRef(false);

  // Edit of an approved (past/completed) dispatch — permission-based, and always
  // for Admin / Super Admin. Opens a request modal that submits for approval.
  const isAdmin = ['ADMIN', 'SUPER_ADMIN'].includes(
    ((user?.role as any)?.name || '').toUpperCase(),
  );
  const canEditPastDispatch = hasPermission('dispatch:edit_past') || isAdmin;
  
  // New Outward Form State
  const [showOutwardForm, setShowOutwardForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState<GateEntry | null>(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  
  const [outwardFormData, setOutwardFormData] = useState({
    warehouseId: user?.activeWarehouseId || 0,
    clientId: 0,
    invoiceNumber: '',
    gatePassNumber: '',
    expectedQuantity: '' as number | '',
    remarks: '',
  });

  // Infinite query for outward gate entries with pagination
  const {
    data: gateEntriesData,
    isLoading: gateEntriesLoading,
    isError: gateEntriesError,
    error: gateEntriesErrorData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteGateEntries({
    shipmentType: 'outward',
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: searchTerm || undefined,
    limit: 25,
  });

  const gateEntries: GateEntry[] = useMemo(() => {
    if (!gateEntriesData) return [];
    const allEntries = gateEntriesData.pages.flatMap(page => {
      const data = page.data;
      // Handle both array and object with items property
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.items)) return data.items;
      return [];
    });
    
    // Filter out gate entries that have dispatches in security_pending status
    // These should not appear in outward process anymore - they're waiting for security
    return allEntries.filter((entry: GateEntry) => {
      // If no dispatch, show in outward process
      if (!entry.dispatchId) return true;
      // If dispatch is security_pending, don't show (it's moved to security check)
      if (entry.dispatchStatus === 'security_pending') return false;
      // Show all other statuses
      return true;
    });
  }, [gateEntriesData]);

  const assignDockMutation = useAssignDock();
  
  // Debounce search term for client API calls
  const debouncedSearchTerm = useDebounce(clientSearchTerm, 300);
  
  // Fetch clients when dropdown is open or when searching
  const { data: clientsResponse, isLoading: clientsLoading, error: clientsError } = useClients(
    debouncedSearchTerm.trim()
      ? { search: debouncedSearchTerm.trim(), status: ['active'] }
      : { status: ['active'] },
    {
      enabled: showClientDropdown,
    }
  );
  
  const clients = clientsResponse?.data || [];
  
  // Create outward gate entry mutation
  const createOutwardEntryMutation = useMutation({
    mutationFn: gateEntryService.createGateEntry,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
      setShowOutwardForm(false);
      setFormError(null);
      resetOutwardForm();
      
      const gateEntryNumber = response.data?.entryNumber || 
        `GOUT-${new Date().getFullYear()}-${String(response.data?.id || Date.now()).padStart(5, '0')}`;
      toast.success(
        `Outbound Entry Created! Entry #${gateEntryNumber} has been registered successfully.`
      );
    },
    onError: (error: any) => {
      setFormError(error?.message || 'Failed to create outward entry');
    },
  });
  
  // Update outward gate entry mutation
  const updateOutwardEntryMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      gateEntryService.updateGateEntry(id.toString(), data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
      setShowOutwardForm(false);
      setFormError(null);
      resetOutwardForm();
      
      const gateEntryNumber = response.data?.entryNumber || 
        `GOUT-${new Date().getFullYear()}-${String(response.data?.id || Date.now()).padStart(5, '0')}`;
      toast.success(
        `Outbound Entry Updated! Entry #${gateEntryNumber} has been updated successfully.`
      );
    },
    onError: (error: any) => {
      setFormError(error?.message || 'Failed to update outward entry');
    },
  });

  const getErrorMessage = (error: any, fallback: string) => {
    if (error?.errors && Array.isArray(error.errors)) {
      return error.errors.join(', ');
    }
    return error?.message || fallback;
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
  };
  
  // Reset outward form
  const resetOutwardForm = () => {
    setOutwardFormData({
      warehouseId: user?.activeWarehouseId || 0,
      clientId: 0,
      invoiceNumber: '',
      gatePassNumber: '',
      expectedQuantity: '',
      remarks: '',
    });
    setClientSearchTerm('');
    setSelectedClient(null);
    setShowClientDropdown(false);
    setErrors({});
    setFormError(null);
    setIsEditMode(false);
    setEditingEntry(null);
  };
  
  // Handle edit outward entry
  const handleEditEntry = (entry: GateEntry) => {
    setIsEditMode(true);
    setEditingEntry(entry);
    
    // Set client - use entry.client if available, otherwise create minimal object for display
    if (entry.client) {
      setSelectedClient(entry.client);
    } else if (entry.clientName) {
      // Fallback: create a minimal client object for display purposes
      setSelectedClient({
        id: entry.clientId,
        companyName: entry.clientName,
        tenantId: 0,
        warehouseId: null,
        registrationNumber: null,
        gstNumber: null,
        panNumber: null,
        address: '',
        city: '',
        state: '',
        pinCode: '',
        country: '',
        contactPersonName: '',
        designation: null,
        email: '',
        phone: '',
        alternatePhone: null,
        clientType: 'per_pallet' as const,
        palletCapacity: null,
        sqftCapacity: null,
        status: 'active' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
    
    setOutwardFormData({
      warehouseId: entry.warehouseId,
      clientId: entry.clientId,
      invoiceNumber: entry.invoiceNumber || '',
      gatePassNumber: entry.packingSlipNumber || '',
      expectedQuantity: entry.expectedQuantity || '',
      remarks: entry.remarks || '',
    });
    setShowOutwardForm(true);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle client selection
  const handleClientSelect = (client: Client) => {
    if (!client?.id) {
      console.error('Invalid client selected:', client);
      return;
    }
    
    setSelectedClient(client);
    setOutwardFormData({ ...outwardFormData, clientId: client.id });
    setClientSearchTerm('');
    setShowClientDropdown(false);
    
    // Clear client validation error when client is selected
    if (formError) {
      setFormError(null);
    }
  };

  // Handle search input change
  const handleClientSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setClientSearchTerm(value);
    
    if (!showClientDropdown) {
      setShowClientDropdown(true);
    }
  };

  // Handle keyboard events
  const handleClientSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowClientDropdown(false);
      setClientSearchTerm('');
    }
    // Enter in the search field must not submit the create form (which clears the search)
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };
  
  // Input change handler with automatic error clearing
  const handleOutwardInputChange = (field: string, value: any) => {
    setOutwardFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  
  // Specialized handlers for specific fields
  const handleInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.invoiceFormat(e.target.value);
    handleOutwardInputChange('invoiceNumber', sanitized);
  };

  const handleRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const sanitized = INPUT_SANITIZERS.remarksFormat(e.target.value);
    handleOutwardInputChange('remarks', sanitized);
  };
  
  // Validate outward form
  const validateOutwardForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    setFormError(null);

    // Client validation
    if (!outwardFormData.clientId || !selectedClient) {
      setFormError('Please search and select a client');
      return false;
    }

    // Invoice Number validation
    const invoiceError = FIELD_VALIDATORS.invoiceNumber(outwardFormData.invoiceNumber, true);
    if (invoiceError) newErrors.invoiceNumber = invoiceError;

    // Gate Pass Number validation (optional)
    if (outwardFormData.gatePassNumber && outwardFormData.gatePassNumber.length > 50) {
      newErrors.gatePassNumber = 'Gate pass number must not exceed 50 characters';
    }
    
    // Remarks validation (optional field)
    const remarksError = FIELD_VALIDATORS.remarks(outwardFormData.remarks, false);
    if (remarksError) newErrors.remarks = remarksError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle outward form submission
  const handleOutwardFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateOutwardForm()) {
      return;
    }

    if (isEditMode && editingEntry) {
      // Update existing entry
      const updateData = {
        warehouseId: outwardFormData.warehouseId,
        clientId: outwardFormData.clientId,
        ...(outwardFormData.expectedQuantity !== '' && { expectedQuantity: Number(outwardFormData.expectedQuantity) }),
        invoiceNumber: outwardFormData.invoiceNumber,
        ...(outwardFormData.gatePassNumber && { packingSlipNumber: outwardFormData.gatePassNumber }),
        ...(outwardFormData.remarks && { remarks: outwardFormData.remarks }),
      };
      
      updateOutwardEntryMutation.mutate({
        id: typeof editingEntry.id === 'string' ? parseInt(editingEntry.id) : editingEntry.id,
        data: updateData,
      });
    } else {
      // Create new entry
      // Generate a unique placeholder vehicle number that passes validation
      const timestamp = Date.now().toString().slice(-6);
      const placeholderVehicle = `PENDING${timestamp}`; // e.g., PENDING123456 (13 chars, valid format)

      const gateEntryData = {
        warehouseId: outwardFormData.warehouseId,
        clientId: outwardFormData.clientId,
        shipmentType: 'outward' as const,
        expectedQuantity: outwardFormData.expectedQuantity === '' ? 0 : Number(outwardFormData.expectedQuantity),
        invoiceNumber: outwardFormData.invoiceNumber,
        ...(outwardFormData.gatePassNumber && { packingSlipNumber: outwardFormData.gatePassNumber }),
        ...(outwardFormData.remarks && { remarks: outwardFormData.remarks }),
        // Valid placeholder values that pass backend validation
        vehicleNumber: placeholderVehicle, // 13 chars, uppercase alphanumeric
        driverName: 'PENDING ASSIGNMENT',
        driverPhone: '9999999999', // Valid 10-digit phone format
      };

      createOutwardEntryMutation.mutate(gateEntryData);
    }
  };

  // Handle dock assignment
  const handleAssignDock = async (entry: GateEntry) => {
    const dockNumber = dockNumbers[entry.id];
    
    if (!dockNumber || !dockNumber.trim()) {
      toast.error('Please enter a dock number');
      return;
    }

    try {
      await assignDockMutation.mutateAsync({
        id: entry.id.toString(),
        dockNumber: dockNumber.trim(),
      });
      
      // Clear the dock number input
      setDockNumbers(prev => {
        const updated = { ...prev };
        delete updated[entry.id];
        return updated;
      });
      
      toast.success(`Dock ${dockNumber} assigned successfully`);
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to assign dock'));
    }
  };

  // Handle create dispatch - navigate to dispatch page
  const handleCreateDispatch = (entry: GateEntry) => {
    navigate(`/dispatch-create/${entry.id}`);
  };

  // Editing a completed (approved) dispatch opens a dedicated past-edit page —
  // changes are staged locally and submitted as one request for an
  // Admin/Super Admin to review. Nothing touches live inventory until approved.
  const handleEditPastDispatch = (entry: GateEntry) => {
    if (!entry.dispatchId) return;
    navigate(`/dispatch-past-edit/${entry.dispatchId}`);
  };

  // Update dock number input
  const handleDockNumberChange = (entryId: string | number, value: string) => {
    setDockNumbers(prev => ({
      ...prev,
      [entryId]: value,
    }));
  };

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Calculate stats from fetched data
  const stats = useMemo(() => {
    const pending = gateEntries.filter((e: GateEntry) => e.status === 'pending_dock_assignment').length;
    const assigned = gateEntries.filter((e: GateEntry) => e.status === 'dock_assigned').length;
    const dispatchCreated = gateEntries.filter((e: GateEntry) => 
      e.status === 'pick_slip_created' || e.status === 'dispatched'
    ).length;
    
    return { pending, assigned, dispatchCreated };
  }, [gateEntries]);

  // Handle selected entry from navigation state (from Security Check)
  useEffect(() => {
    const state = location.state as { selectedEntry?: GateEntry; message?: string } | null;
    if (state?.selectedEntry && !hasProcessedState.current) {
      hasProcessedState.current = true;
      const entryId = state.selectedEntry.id;
      
      // Invalidate and refetch gate entries to get latest data
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
      
      setHighlightedId(entryId);
      
      // Show message if provided
      if (state.message) {
        toast.success(state.message);
      }
      
      // Clear navigation state immediately to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
      
      // Scroll to highlighted row after a short delay to ensure rendering
      setTimeout(() => {
        highlightedRowRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 500);
      
      // Clear highlight after 3 seconds
      setTimeout(() => {
        setHighlightedId(null);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);



  // Loading state
  if (gateEntriesLoading) {
    return (
      <Layout>
        <div className="min-h-screen p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading outward data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (gateEntriesError) {
    return (
      <Layout>
        <div className="min-h-screen p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center bg-white p-10 rounded-2xl shadow-2xl border-2 border-red-200">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 font-semibold text-lg mb-2">Failed to load outward entries</p>
            <p className="text-gray-600 text-sm mb-4">
              {getErrorMessage(gateEntriesErrorData, 'Something went wrong')}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow-md transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Outbound Process</h2>
                <p className="text-gray-600 mt-1">Dock assignment and dispatch creation for outbound shipments</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowOutwardForm(true);
                resetOutwardForm();
              }}
              className="w-full lg:w-auto justify-center px-6 py-3 min-h-[44px] lg:min-h-0 bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-xl hover:from-brand-700 hover:to-brand-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="whitespace-nowrap">Create Outbound Entry</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Pending Dock Assignment</p>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.pending}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Dock Assigned</p>
              <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.assigned}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-600">Dispatch Created</p>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{stats.dispatchCreated}</p>
          </div>
        </div>

        {/* Outward Entry Form Modal */}
        {showOutwardForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
            <div className="bg-white rounded-2xl lg:rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] lg:max-h-[95vh] overflow-hidden animate-slide-up lg:animate-none flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        {isEditMode ? 'Edit Outbound Entry' : 'Create Outbound Entry'}
                      </h2>
                      <p className="text-brand-100 text-sm mt-1">
                        {isEditMode ? 'Update outbound shipment details' : 'Register new outbound shipment details'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowOutwardForm(false);
                      resetOutwardForm();
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto flex-1 min-h-0 lg:flex-none lg:max-h-[calc(95vh-120px)] bg-gray-50">
                <form onSubmit={handleOutwardFormSubmit} className="p-8 space-y-6">
                  {/* Client Selection */}
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center mb-4">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <h3 className="text-lg font-semibold text-gray-900">Client Information</h3>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Client Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative" ref={clientDropdownRef}>
                        {/* Search Input */}
                        <div className="relative">
                          <input
                            type="text"
                            placeholder={selectedClient ? selectedClient.companyName : "Type to search clients..."}
                            className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent transition-colors duration-200 ${
                              formError && !selectedClient
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-brand-500'
                            }`}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-form-type="other"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            value={selectedClient ? selectedClient.companyName : clientSearchTerm}
                            onChange={selectedClient ? undefined : handleClientSearchChange}
                            onFocus={() => {
                              if (!selectedClient) {
                                setShowClientDropdown(true);
                              }
                            }}
                            onClick={() => {
                              if (!selectedClient) {
                                setShowClientDropdown(true);
                              }
                            }}
                            onKeyDown={handleClientSearchKeyDown}
                            readOnly={Boolean(selectedClient)}
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center">
                            {selectedClient ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedClient(null);
                                  setOutwardFormData({ ...outwardFormData, clientId: 0 });
                                  setClientSearchTerm('');
                                  setShowClientDropdown(false);
                                }}
                                className="px-2 py-1 text-gray-400 hover:text-gray-600"
                                title="Clear selection"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            ) : (
                              <div className="px-2 pointer-events-none">
                                {clientsLoading && debouncedSearchTerm ? (
                                  <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Dropdown Panel */}
                        {showClientDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                            <div className="max-h-48 overflow-y-auto">
                              {clientsLoading ? (
                                <div className="px-4 py-6 text-sm text-gray-500 text-center">
                                  <div className="flex items-center justify-center">
                                    <svg className="w-5 h-5 text-gray-400 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {debouncedSearchTerm ? 'Searching clients...' : 'Loading clients...'}
                                  </div>
                                </div>
                              ) : clientsError ? (
                                <div className="px-4 py-6 text-sm text-red-500 text-center">
                                  Error loading clients: {clientsError.message || 'Unknown error'}
                                </div>
                              ) : clients.length > 0 ? (
                                <ul className="py-1">
                                  {clients.filter(client => client?.id).map((client: Client) => (
                                    <li key={client.id}>
                                      <button
                                        type="button"
                                        className="w-full text-left px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-50 last:border-b-0"
                                        onClick={() => handleClientSelect(client)}
                                      >
                                        <div className="font-medium text-gray-900">{client.companyName || 'Unknown Client'}</div>
                                        {client.contactPersonName && (
                                          <div className="text-sm text-gray-600">Contact: {client.contactPersonName}</div>
                                        )}
                                        {client.email && (
                                          <div className="text-xs text-gray-500">{client.email}</div>
                                        )}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="px-4 py-6 text-sm text-gray-500 text-center">
                                  {debouncedSearchTerm ? `No clients found matching "${debouncedSearchTerm}"` : 'No clients available'}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Selected client confirmation */}
                      {selectedClient && (
                        <div className="mt-2 text-sm text-brand-600 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Selected: {selectedClient.companyName}
                        </div>
                      )}
                      
                      {/* Client validation error */}
                      {formError && !selectedClient && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formError}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Shipment Details */}
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center mb-4">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="text-lg font-semibold text-gray-900">Shipment Details</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        label="Invoice Number"
                        value={outwardFormData.invoiceNumber}
                        onChange={handleInvoiceNumberChange}
                        required
                        error={errors.invoiceNumber}
                        placeholder="e.g., INV2024001 or ABC123"
                        maxLength={20}
                      />

                      <Input
                        label="Gate Pass Number"
                        value={outwardFormData.gatePassNumber}
                        onChange={(e) => handleOutwardInputChange('gatePassNumber', e.target.value)}
                        error={errors.gatePassNumber}
                        placeholder="Gate pass number"
                        maxLength={50}
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Expected Quantity
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="Number of units/pallets"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors duration-200"
                          value={outwardFormData.expectedQuantity}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '');
                            setOutwardFormData({ ...outwardFormData, expectedQuantity: digits === '' ? '' : Number(digits) });
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center mb-4">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      <h3 className="text-lg font-semibold text-gray-900">Additional Notes</h3>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remarks & Special Instructions
                        <span className="text-gray-500 text-xs ml-2">
                          ({outwardFormData.remarks.length}/200 characters)
                        </span>
                      </label>
                      <textarea
                        rows={4}
                        placeholder="Letters, numbers, and spaces only. Max 200 characters."
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent ${
                          errors.remarks ? 'border-red-500' : 'border-gray-300'
                        }`}
                        value={outwardFormData.remarks}
                        onChange={handleRemarksChange}
                        maxLength={200}
                      />
                      {errors.remarks && (
                        <p className="mt-1 text-sm text-red-600">{errors.remarks}</p>
                      )}
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOutwardForm(false);
                        resetOutwardForm();
                      }}
                      className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
                      disabled={createOutwardEntryMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 text-white bg-brand-600 hover:bg-brand-700 rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      disabled={createOutwardEntryMutation.isPending || updateOutwardEntryMutation.isPending || clientsLoading}
                    >
                      {(createOutwardEntryMutation.isPending || updateOutwardEntryMutation.isPending) ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {isEditMode ? 'Updating Entry...' : 'Creating Entry...'}
                        </>
                      ) : (
                        <>
                          {isEditMode ? 'Update Outbound Entry' : 'Create Outbound Entry'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Outward Entries Table */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-brand-300">
          <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">
                Outbound Entries
                {gateEntries.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({gateEntries.length} {gateEntries.length === 1 ? 'entry' : 'entries'})
                  </span>
                )}
              </h3>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="p-5 bg-gray-50 border-b-2 border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {searchOpen ? (
                <DebouncedSearchInput
                  placeholder="Search by entry number, vehicle, driver, or client..."
                  onSearch={handleSearch}
                  debounceMs={300}
                  onClose={() => { setSearchOpen(false); handleSearch(''); }}
                />
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search
                </button>
              )}
              
              <ResponsiveSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter((val || 'all') as StatusFilter)}
                includeEmpty={false}
                placeholder="All Status"
                className="px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'pending_dock_assignment', label: 'Pending Dock Assignment' },
                  { value: 'dock_assigned', label: 'Dock Assigned' },
                  { value: 'pick_slip_created', label: 'Pick Slip Created' },
                  { value: 'dispatched', label: 'Dispatched' },
                ]}
              />
            </div>
          </div>

          <div className="px-1 py-3 lg:p-5">
            <ResponsiveTable
              columns={[
                {
                  key: 'entryNumber',
                  label: 'Entry Number',
                  width: '140px',
                  render: (value) => (
                    <div className="font-medium text-gray-900" title={value}>
                      {value}
                    </div>
                  )
                },
                {
                  key: 'createdAt',
                  label: 'Date',
                  width: '100px',
                  render: (value) => (
                    <div className="text-sm text-gray-900">
                      {value ? new Date(value).toLocaleDateString() : '-'}
                    </div>
                  )
                },
                {
                  key: 'vehicleNumber',
                  label: 'Vehicle',
                  width: '120px',
                  render: (value) => (
                    <div className="font-medium text-gray-900" title={value}>
                      {value}
                    </div>
                  )
                },
                {
                  key: 'driverName',
                  label: 'Driver',
                  width: '130px',
                  render: (value) => (
                    <div className="max-w-[130px] truncate" title={value || ''}>
                      {value || '-'}
                    </div>
                  )
                },
                {
                  key: 'clientName',
                  label: 'Client',
                  width: '150px',
                  render: (value) => (
                    <div className="max-w-[150px] truncate" title={value || ''}>
                      {value || '-'}
                    </div>
                  )
                },
                {
                  key: 'expectedQuantity',
                  label: 'Expected Qty',
                  width: '100px',
                  render: (value) => (
                    <div className="font-semibold text-gray-900">{value}</div>
                  )
                },
                {
                  key: 'dockNumber',
                  label: 'Dock',
                  width: '80px',
                  render: (value) => (
                    <div className="font-medium text-gray-900">
                      {value || '-'}
                    </div>
                  )
                },
                {
                  key: 'status',
                  label: 'Status',
                  width: '150px',
                  render: (value, entry) => {
                    // A reopened Past Dispatch takes precedence in the badge.
                    if (entry.dispatchReopenedForEdit) {
                      return (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap bg-amber-100 text-amber-800">
                          Past Dispatch Resubmitted
                        </span>
                      );
                    }
                    // If dispatch exists, show dispatch status
                    if (entry.dispatchStatus) {
                      const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
                        draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Dispatch Draft' },
                        submitted: { bg: 'bg-brand-100', text: 'text-brand-800', label: 'Dispatch Submitted' },
                        approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Dispatch Approved' },
                        security_pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Security Pending' },
                        rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Dispatch Rejected' },
                        cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Dispatch Cancelled' },
                      };
                      const config = statusConfig[entry.dispatchStatus] || statusConfig.draft;
                      return (
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap ${config.bg} ${config.text}`}>
                          {config.label}
                        </span>
                      );
                    }
                    
                    // Otherwise show gate entry status
                    return (
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap
                        ${value === 'pending_dock_assignment' ? 'bg-yellow-100 text-yellow-800' : 
                          value === 'dock_assigned' ? 'bg-brand-100 text-brand-800' :
                          value === 'pick_slip_created' ? 'bg-brand-100 text-brand-800' :
                          value === 'dispatched' ? 'bg-green-100 text-green-800' : 
                          'bg-gray-100 text-gray-800'}`}
                      >
                        {value === 'pending_dock_assignment' ? 'Pending Dock' :
                         value === 'dock_assigned' ? 'Dock Assigned' :
                         value === 'pick_slip_created' ? 'Pick Slip Created' :
                         value === 'dispatched' ? 'Dispatched' :
                         value}
                      </span>
                    );
                  }
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  width: '280px',
                  render: (_, entry) => (
                    <div className="flex items-center justify-center space-x-2 whitespace-nowrap">
                      {/* Case 1: Pending Dock Assignment - Show Edit and Assign Dock */}
                      {entry.status === 'pending_dock_assignment' && (
                        <>
                          <button
                            onClick={() => handleEditEntry(entry)}
                            className="px-3 py-1 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 flex items-center"
                            title="Edit Outbound Entry"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          <input
                            type="text"
                            placeholder="Dock #"
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            value={dockNumbers[entry.id] || ''}
                            onChange={(e) => handleDockNumberChange(entry.id, e.target.value)}
                            disabled={assignDockMutation.isPending}
                          />
                          <button
                            onClick={() => handleAssignDock(entry)}
                            disabled={assignDockMutation.isPending}
                            className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {assignDockMutation.isPending ? 'Assigning...' : 'Assign Dock'}
                          </button>
                        </>
                      )}
                      
                      {/* Case 2: Dock Assigned - Show Create Dispatch (No Edit) */}
                      {entry.status === 'dock_assigned' && (
                        <button
                          onClick={() => handleCreateDispatch(entry)}
                          disabled={!hasPermission('dispatch:create')}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Create Dispatch
                        </button>
                      )}
                      
                      {/* Case 3: Pick Slip Created or Dispatched - Open Dispatch (No Edit) */}
                      {(entry.status === 'pick_slip_created' || entry.status === 'dispatched') && (
                        <button
                          onClick={() => handleCreateDispatch(entry)}
                          className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 flex items-center"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Open Dispatch
                        </button>
                      )}

                      {/* Case 4: Completed (approved/past) dispatch — request an edit.
                          Only for users with dispatch:edit_past or Admin / Super Admin. */}
                      {entry.status === 'dispatched' &&
                        (entry as any).dispatchStatus === 'approved' &&
                        (entry as any).dispatchId &&
                        canEditPastDispatch && (
                          <button
                            onClick={() => handleEditPastDispatch(entry)}
                            className="px-3 py-1 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 flex items-center"
                            title="Edit this completed dispatch (requires Admin approval)"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                        )}

                      {/* Available regardless of status (draft, pending, approved,
                          etc.) as long as the user holds dispatch:print (Admin/Super
                          Admin always get it, same as canEditPastDispatch above). */}
                      {(hasPermission('dispatch:print') || isAdmin) && (
                        <button
                          onClick={() => setPrintEntry(entry)}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded border border-gray-300 hover:bg-gray-200 flex items-center"
                          title="Print entry details"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m10-10V5a2 2 0 00-2-2H9a2 2 0 00-2 2v2m10 0H7m10 0v10a2 2 0 01-2 2H9a2 2 0 01-2-2V7" />
                          </svg>
                          Print
                        </button>
                      )}
                    </div>
                  )
                }
              ]}
              data={gateEntries}
              height="600px"
              emptyMessage="No outward entries found. New entries will appear here from the security gate."
              highlightedRowId={highlightedId}
              getRowRef={(rowId) => rowId === highlightedId ? highlightedRowRef : null}
              onLoadMore={handleLoadMore}
              hasMore={hasNextPage}
              isLoading={isFetchingNextPage}
            />
          </div>
        </div>
      </div>
      {printEntry && <GateEntryPrintView entry={printEntry} onClose={() => setPrintEntry(null)} />}
    </Layout>
  );
}
